import React, { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import {
  KeyIcon,
  UserIcon,
  LockClosedIcon,
  SparklesIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline'
import { AlertModal } from '@/components/ConfirmModal'

interface RegisterProps {
  onSwitchToLogin: () => void
}

export const Register: React.FC<RegisterProps> = ({ onSwitchToLogin }) => {
  const { registerWithInvite, isLoading } = useAuth()
  
  const [inviteCode, setInviteCode] = useState('')
  const [name, setName] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  
  const [alertDialog, setAlertDialog] = useState<{
    isOpen: boolean
    title?: string
    message: string
    type?: 'success' | 'error' | 'info'
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  })

  // Auto-fill code from URL query if present (e.g. ?code=INV-1234)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const codeParam = params.get('code')
    if (codeParam) {
      setInviteCode(codeParam.toUpperCase())
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!inviteCode.trim()) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'خطا',
        message: 'لطفاً کد دعوت را وارد کنید.'
      })
      return
    }

    if (!name.trim() || !username.trim() || !password) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'خطا',
        message: 'لطفاً تمام فیلدها را تکمیل نمایید.'
      })
      return
    }

    if (password.length < 4) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'رمز عبور کوتاه',
        message: 'رمز عبور باید حداقل ۴ کاراکتر باشد.'
      })
      return
    }

    if (password !== confirmPassword) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'عدم تطابق رمز',
        message: 'رمز عبور و تکرار آن یکسان نیستند.'
      })
      return
    }

    try {
      await registerWithInvite(
        inviteCode.trim().toUpperCase(),
        name.trim(),
        username.trim(),
        password
      )
    } catch (err: any) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'خطا در ثبت‌نام',
        message: err.response?.data?.detail || 'کد دعوت نامعتبر است یا مشکلی در ثبت‌نام پیش آمده است.'
      })
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-4">
      <div className="w-full max-w-md space-y-6 animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-emerald-600 text-white shadow-xl shadow-emerald-500/20 mb-2">
            <SparklesIcon className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-extrabold text-slate-800 dark:text-white">
            ثبت‌نام با کد دعوت
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            برای فعال‌سازی حساب کاربری خود کد دعوت دریافتی را وارد کنید
          </p>
        </div>

        {/* Register Form */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-xl border border-slate-100 dark:border-slate-700/60 space-y-4">
          <form onSubmit={handleSubmit} className="space-y-3.5">
            {/* Invite Code */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                کد دعوت:
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={inviteCode}
                  onChange={e => setInviteCode(e.target.value.toUpperCase())}
                  placeholder="مثال: INV-7824"
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl pr-10 pl-3 py-2.5 text-xs font-mono font-bold tracking-wider text-slate-800 dark:text-white outline-none focus:border-emerald-500 uppercase"
                  required
                />
                <KeyIcon className="w-4 h-4 text-emerald-500 absolute right-3.5 top-3" />
              </div>
            </div>

            {/* Full Name */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                نام و نام خانوادگی:
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="مثال: علی رضایی"
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl pr-10 pl-3 py-2.5 text-xs font-semibold text-slate-800 dark:text-white outline-none focus:border-emerald-500"
                  required
                />
                <UserIcon className="w-4 h-4 text-slate-400 absolute right-3.5 top-3" />
              </div>
            </div>

            {/* Username */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                نام کاربری (انگلیسی):
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value.toLowerCase())}
                  placeholder="مثال: ali_rezaei"
                  dir="ltr"
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl pr-10 pl-3 py-2.5 text-xs font-semibold text-slate-800 dark:text-white outline-none focus:border-emerald-500 text-left"
                  required
                />
                <UserIcon className="w-4 h-4 text-slate-400 absolute right-3.5 top-3" />
              </div>
            </div>

            {/* Password */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                  رمز عبور:
                </label>
                <div className="relative">
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    dir="ltr"
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl px-3 py-2.5 text-xs font-semibold text-slate-800 dark:text-white outline-none focus:border-emerald-500 text-left"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                  تکرار رمز:
                </label>
                <div className="relative">
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    dir="ltr"
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl px-3 py-2.5 text-xs font-semibold text-slate-800 dark:text-white outline-none focus:border-emerald-500 text-left"
                    required
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-bold text-xs shadow-lg shadow-emerald-600/20 transition active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 mt-2"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              {isLoading ? 'در حال ثبت‌نام...' : 'تکمیل ثبت‌نام و ورود'}
            </button>
          </form>

          {/* Footer switch */}
          <div className="pt-3 border-t border-slate-100 dark:border-slate-700 text-center">
            <button
              type="button"
              onClick={onSwitchToLogin}
              className="text-xs text-emerald-600 dark:text-emerald-400 font-bold hover:underline"
            >
              قبلاً ثبت‌نام کرده‌اید؟ ورود به حساب
            </button>
          </div>
        </div>
      </div>

      <AlertModal
        isOpen={alertDialog.isOpen}
        title={alertDialog.title}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  )
}

export default Register
